// Cena: tudo que é desenhado no mundo vira um Drawable com uma chave de profundidade
// (a linha do "pé" no chão). Quem está mais embaixo na tela é desenhado por cima — é
// isso que faz o personagem ficar atrás da mesa e na frente da cadeira.

import { resolveToken } from '../art/palette.js';
import type { ScreenMode } from '../world/behavior.js';
import type { Camera, Viewport } from './camera.js';

export interface Drawable {
  /** Profundidade: y do pé no mundo (px de arte). Maior = mais na frente. */
  sortY: number;
  /** Desenha em coordenadas de mundo (o contexto já está transformado pela câmera). */
  draw(ctx: CanvasRenderingContext2D, now: number): void;
}

/** Algo desenhado por cima de tudo, em px de tela (nomes, balões): fica nítido em qualquer zoom. */
export type Overlay = (ctx: CanvasRenderingContext2D, toScreen: (wx: number, wy: number) => { x: number; y: number }, now: number) => void;

export interface Layer {
  /** Fundo estático (pisos e paredes já assados), desenhado antes dos Drawables. */
  background?: Array<{ canvas: HTMLCanvasElement; x: number; y: number }>;
  drawables: Drawable[];
  /** Depois dos Drawables, ainda em mundo (escurecer de noite, brilhos). */
  after?: Array<(ctx: CanvasRenderingContext2D, now: number) => void>;
  overlays?: Overlay[];
}

export function renderScene(ctx: CanvasRenderingContext2D, camera: Camera, view: Viewport, layer: Layer, now: number, clear = '#0e1016'): void {
  ctx.setTransform(1, 0, 0, 1, 0, 0);
  ctx.imageSmoothingEnabled = false;
  ctx.fillStyle = clear;
  ctx.fillRect(0, 0, view.w, view.h);

  const { ox, oy } = camera.origin(view);
  ctx.setTransform(camera.zoom, 0, 0, camera.zoom, ox, oy);
  for (const bg of layer.background ?? []) ctx.drawImage(bg.canvas, bg.x, bg.y);
  const sorted = [...layer.drawables].sort((a, b) => a.sortY - b.sortY);
  for (const d of sorted) d.draw(ctx, now);
  for (const fn of layer.after ?? []) fn(ctx, now);

  ctx.setTransform(1, 0, 0, 1, 0, 0);
  const toScreen = (wx: number, wy: number) => camera.toScreen(wx, wy, view);
  for (const overlay of layer.overlays ?? []) overlay(ctx, toScreen, now);
}

// ---------------------------------------------------------------- telas dos monitores

/** Retângulo de tela, em px de mundo (1 px de arte = 1 px do mundo; a câmera só amplia). */
export interface ScreenRect {
  x: number;
  y: number;
  w: number;
  h: number;
}

const ink = (token: string) => resolveToken(token) ?? '#ff00ff';

function fill(c: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, token: string): void {
  c.fillStyle = ink(token);
  c.fillRect(x, y, w, h);
}

/** Linha de texto de 1 px de altura, com um respiro a cada 4 px (cara de letra, sem ler). */
function text(c: CanvasRenderingContext2D, x: number, y: number, len: number, token: string): void {
  c.fillStyle = ink(token);
  for (let i = 0; i < len; i++) if (i % 4 !== 3) c.fillRect(x + i, y, 1, 1);
}

/** Comprimentos das linhas de texto; cada linha ocupa 2 px (a linha e o respiro). */
const TEXT_LINES = [9, 5, 12, 7, 3, 10, 6, 11, 4, 8];
/** Linhas do terminal: pares de comando (prompt verde) e saída. */
const TERMINAL_LINES = [6, 10, 4, 8, 12, 5];

/** Texto rolando para cima (lendo): 2 px por quadro, a linha passa inteira. */
function paintRead(c: CanvasRenderingContext2D, b: ScreenRect, frame: number): void {
  fill(c, b.x, b.y, b.w, b.h, 'screen0');
  const period = TEXT_LINES.length * 2;
  const shift = (frame * 2) % period;
  for (let row = 1; row < b.h - 1; row++) {
    const k = (row - 1 + shift) % period;
    if (k % 2) continue;
    const indent = (k / 2) % 3 === 1 ? 2 : 0;
    text(c, b.x + 2 + indent, b.y + row, Math.min(TEXT_LINES[k / 2]!, b.w - 4 - indent), 'screen3');
  }
}

/** Linhas aparecendo, uma a uma, com o cursor piscando no fim da última. */
function paintWrite(c: CanvasRenderingContext2D, b: ScreenRect, frame: number): void {
  fill(c, b.x, b.y, b.w, b.h, 'screen0');
  const rows = Math.floor((b.h - 2) / 2);
  const count = Math.min(frame + 1, rows);
  for (let i = 0; i < count; i++) {
    const indent = i % 2 ? 2 : 0;
    const len = Math.min(TEXT_LINES[(i * 3) % TEXT_LINES.length]!, b.w - 4 - indent);
    const y = b.y + 1 + i * 2;
    text(c, b.x + 2 + indent, y, len, 'screen3');
    if (i === count - 1 && frame % 2 === 0) fill(c, b.x + 2 + indent + len + 1, y, 2, 1, 'white3');
  }
}

/** Terminal: fundo escuro, prompt verde e saída, com as linhas subindo. */
function paintTerminal(c: CanvasRenderingContext2D, b: ScreenRect, frame: number): void {
  fill(c, b.x, b.y, b.w, b.h, 'black0');
  const period = TERMINAL_LINES.length * 2;
  const shift = (frame * 2) % period;
  for (let row = 1; row < b.h - 1; row++) {
    const k = (row - 1 + shift) % period;
    if (k % 2) continue;
    const i = k / 2;
    const y = b.y + row;
    if (i % 2 === 0) {
      fill(c, b.x + 1, y, 2, 1, 'leaf3');
      text(c, b.x + 4, y, Math.min(TERMINAL_LINES[i]!, b.w - 6), 'white1');
    } else {
      text(c, b.x + 2, y, Math.min(TERMINAL_LINES[i]!, b.w - 4), 'metal2');
    }
  }
}

/** Janela de navegador: barra de título e de endereço, e um bloco e uma barra carregando. */
function paintWeb(c: CanvasRenderingContext2D, b: ScreenRect, frame: number): void {
  fill(c, b.x, b.y, b.w, 1, 'metal1');
  fill(c, b.x + 1, b.y, 1, 1, 'red2');
  fill(c, b.x + 2, b.y, 1, 1, 'light2');
  fill(c, b.x + 3, b.y, 1, 1, 'leaf2');
  fill(c, b.x, b.y + 1, b.w, 1, 'metal2');
  fill(c, b.x + 5, b.y + 1, Math.max(0, b.w - 8), 1, 'white2');
  fill(c, b.x, b.y + 2, b.w, b.h - 2, 'white3');
  fill(c, b.x + 2, b.y + 3, Math.min(b.w - 4, 2 + frame * 2), 2, 'fabricBlue3');
  fill(c, b.x + 2, b.y + b.h - 2, b.w - 4, 1, 'paper1');
  fill(c, b.x + 2, b.y + b.h - 2, Math.round(((b.w - 4) * (frame + 1)) / 4), 1, 'fabricBlue2');
}

/** Três pontinhos pulsando, um por vez. */
function paintThinking(c: CanvasRenderingContext2D, b: ScreenRect, frame: number): void {
  fill(c, b.x, b.y, b.w, b.h, 'screen0');
  const cx = b.x + Math.floor(b.w / 2);
  const cy = b.y + Math.floor(b.h / 2) - 1;
  [-3, 0, 3].forEach((dx, i) => fill(c, cx + dx - 1, cy, 2, 2, i === frame % 3 ? 'screen3' : 'screen2'));
}

/** Um ✓ verde, de 2 px de espessura, no centro. */
function paintDone(c: CanvasRenderingContext2D, b: ScreenRect): void {
  fill(c, b.x, b.y, b.w, b.h, 'screen0');
  const cx = b.x + Math.floor(b.w / 2) - 3;
  const cy = b.y + Math.floor(b.h / 2) - 2;
  for (const [dx, dy] of [[0, 2], [1, 3], [2, 4], [3, 3], [4, 2], [5, 1], [6, 0]] as const) fill(c, cx + dx, cy + dy, 1, 2, 'leaf3');
}

/** Tela piscando em âmbar: pede a sua atenção. */
function paintAlert(c: CanvasRenderingContext2D, b: ScreenRect, frame: number): void {
  fill(c, b.x, b.y, b.w, b.h, frame % 2 ? 'light0' : 'light2');
}

/** Protetor de tela escuro: três estrelinhas que acendem devagar, uma por vez. */
function paintSaver(c: CanvasRenderingContext2D, b: ScreenRect, frame: number): void {
  fill(c, b.x, b.y, b.w, b.h, 'screen0');
  for (let i = 0; i < 3; i++) {
    const x = b.x + 2 + ((i * 5 + 1) % Math.max(1, b.w - 4));
    const y = b.y + 2 + ((i * 3 + 1) % Math.max(1, b.h - 4));
    if ((frame + i) % 4 === 0) {
      fill(c, x - 1, y, 3, 1, 'screen2');
      fill(c, x, y - 1, 1, 3, 'screen2');
      fill(c, x, y, 1, 1, 'screen3');
    } else {
      fill(c, x, y, 1, 1, 'screen2');
    }
  }
}

/** Pinta a tela de um monitor no quadro `frame` da animação `mode` (ver screenMode em world/behavior.ts). */
export function paintScreen(c: CanvasRenderingContext2D, b: ScreenRect, mode: ScreenMode, frame: number): void {
  switch (mode) {
    case 'off':
      return fill(c, b.x, b.y, b.w, b.h, 'black0');
    case 'read':
      return paintRead(c, b, frame);
    case 'write':
      return paintWrite(c, b, frame);
    case 'terminal':
      return paintTerminal(c, b, frame);
    case 'web':
      return paintWeb(c, b, frame);
    case 'thinking':
      return paintThinking(c, b, frame);
    case 'done':
      return paintDone(c, b);
    case 'alert':
      return paintAlert(c, b, frame);
    case 'saver':
      return paintSaver(c, b, frame);
  }
}
